import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import WillDraftingModal from '../Modals/WillDraftingModal';
import WillPricingModal from '../Modals/WillPricingModal';
import { useCurrency } from '../../context/CurrencyContext';
import { fetchActivePlans } from '../../services/plansService';

const {
  FiZap, FiFileText, FiShield, FiCheckCircle, FiChevronRight,
  FiLock, FiUsers, FiAlertCircle, FiCpu, FiDownload,
  FiArrowRight, FiStar, FiBook, FiGlobe, FiMail, FiClock,
  FiPlay, FiCircle, FiRefreshCw, FiTrendingUp, FiDollarSign,
  FiPhone, FiX, FiLoader, FiPackage,
} = FiIcons;

// Metadata keyed by plan_key — UI chrome only, no pricing
const PLAN_META = {
  will_draft: {
    icon: FiFileText,
    iconBg: 'bg-blue-50 text-blue-500',
    tagline: 'Generate your will draft instantly',
    highlights: ['AI-guided 4-step interview', '30+ jurisdiction support', 'Downloadable PDF draft', 'Legal next-steps guide'],
    cta: 'Get Started',
    ctaStyle: 'border-2 border-accent-navy text-accent-navy hover:bg-accent-navy hover:text-white',
    highlight: false,
  },
  will_guardian: {
    icon: FiShield,
    iconBg: 'bg-accent-gold/10 text-accent-gold',
    tagline: 'Ongoing will management & legal support',
    highlights: ['Unlimited will revisions', '1× attorney consult / year', 'Life-event update reminders', 'Digital asset vault'],
    badge: 'Most Popular',
    cta: 'Start Free Trial',
    ctaStyle: 'bg-accent-gold text-white hover:bg-accent-gold/90 shadow-lg shadow-accent-gold/30',
    highlight: true,
  },
  will_estate: {
    icon: FiStar,
    iconBg: 'bg-purple-50 text-purple-500',
    tagline: 'Full estate management with human support',
    highlights: ['Dedicated estate concierge', 'Quarterly attorney reviews', 'Family sub-accounts (×4)', 'Trust & POA drafting'],
    cta: 'Contact Us',
    ctaStyle: 'border-2 border-accent-navy text-accent-navy hover:bg-accent-navy hover:text-white',
    highlight: false,
  },
};

const FALLBACK_PLANS = [
  { id: 'will_draft',    plan_key: 'will_draft',    name: 'Will Draft',       price_usd: 29, billing_cycle: 'one-time', sort_order: 10 },
  { id: 'will_guardian', plan_key: 'will_guardian', name: 'Legacy Guardian',  price_usd: 15, billing_cycle: 'month',    sort_order: 11 },
  { id: 'will_estate',   plan_key: 'will_estate',   name: 'Estate Concierge', price_usd: 39, billing_cycle: 'month',    sort_order: 12 },
];

// Formatting helpers
function billingShort(cycle, billing) {
  if (cycle === 'one-time' || cycle === 'forever') return 'one-time';
  if (cycle === 'year') return '/yr';
  return billing === 'annual' ? '/mo, billed annually' : '/mo';
}

// ── Pricing Teaser Card ───────────────────────────────────────────────────
const PricingTeaserCard = ({ plan, meta, fmtPrice, onOpenPricing, currency, billing }) => {
  const isOneTime = plan.billing_cycle === 'one-time' || plan.billing_cycle === 'forever';
  const isMonthlyPlan = plan.billing_cycle === 'month';
  const displayPrice = (isMonthlyPlan && billing === 'annual') ? plan.price_usd * 0.8 : plan.price_usd;
  const isNonUSD  = currency !== 'USD';

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative flex flex-col rounded-[28px] border-2 overflow-hidden transition-all hover:shadow-xl ${
        meta.highlight
          ? 'border-accent-gold shadow-xl shadow-accent-gold/15 scale-[1.02]'
          : 'border-brand-dark hover:border-accent-navy/30'
      }`}
    >
      {meta.badge && (
        <div className="absolute top-4 right-4 bg-accent-gold text-white px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest">
          {meta.badge}
        </div>
      )}

      <div className={`p-6 ${meta.highlight ? 'bg-gradient-to-br from-accent-navy to-[#1a2a5e]' : 'bg-white'}`}>
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${
          meta.highlight ? 'bg-accent-gold/20 text-accent-gold' : meta.iconBg
        }`}>
          <SafeIcon icon={meta.icon} className="w-5 h-5" />
        </div>

        <h4 className={`text-lg font-serif mb-1 ${meta.highlight ? 'text-white' : 'text-accent-navy'}`}>
          {plan.name}
        </h4>
        <p className={`text-[10px] font-light mb-4 ${meta.highlight ? 'text-white/60' : 'text-text-muted'}`}>
          {meta.tagline}
        </p>

        {/* ── Live price from admin ── */}
        <div className="flex items-end gap-1 mb-1 flex-wrap">
          <span className={`text-3xl font-black font-serif ${meta.highlight ? 'text-white' : 'text-accent-navy'}`}>
            {fmtPrice(displayPrice)}
          </span>
          <span className={`text-xs font-light mb-1 ${meta.highlight ? 'text-white/50' : 'text-text-muted'}`}>
            {billingShort(plan.billing_cycle, billing)}
          </span>
        </div>
        {isNonUSD && displayPrice > 0 && (
          <p className={`text-[9px] font-light mb-3 ${meta.highlight ? 'text-white/40' : 'text-text-muted'}`}>
            ${displayPrice.toFixed(2)} USD
          </p>
        )}

        <button
          onClick={onOpenPricing}
          className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${meta.ctaStyle}`}
        >
          {meta.cta} <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 p-5 bg-brand-light border-t border-brand-dark space-y-2.5">
        {(meta.highlights || []).map((h, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-xs text-accent-navy font-medium">{h}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ── Comparison Table ───────────────────────────────────────────────────────
const COMPARE_ROWS = [
  { feature: 'AI-guided interview',     draft: true,         guardian: true,        estate: true        },
  { feature: '30+ jurisdiction support',draft: true,         guardian: true,        estate: true        },
  { feature: 'PDF will draft download', draft: true,         guardian: true,        estate: true        },
  { feature: 'Unlimited revisions',     draft: false,        guardian: true,        estate: true        },
  { feature: 'Annual review reminders', draft: false,        guardian: true,        estate: true        },
  { feature: 'Attorney consultation',   draft: false,        guardian: '1× /yr',    estate: '4× /yr'    },
  { feature: 'Digital asset vault',     draft: false,        guardian: '50 entries',estate: 'Unlimited' },
  { feature: 'Family sub-accounts',     draft: false,        guardian: false,       estate: 'Up to 4'   },
  { feature: 'Trust & POA drafting',    draft: false,        guardian: false,       estate: true        },
  { feature: 'Dedicated concierge',     draft: false,        guardian: false,       estate: true        },
];

const CellValue = ({ val }) => {
  if (val === true)  return <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mx-auto" />;
  if (val === false) return <SafeIcon icon={FiX}           className="w-4 h-4 text-brand-dark mx-auto opacity-40" />;
  return <span className="text-xs font-bold text-accent-navy">{val}</span>;
};

const CompareTable = ({ plans, fmtPrice, onOpenPricing, billing }) => {
  const draftPlan    = plans.find(p => p.plan_key === 'will_draft');
  const guardianPlan = plans.find(p => p.plan_key === 'will_guardian');
  const estatePlan   = plans.find(p => p.plan_key === 'will_estate');

  const getDisplayPrice = (plan) => {
    if (!plan) return 0;
    if (plan.billing_cycle === 'month' && billing === 'annual') return plan.price_usd * 0.8;
    return plan.price_usd;
  };

  const headers = [
    `Will Draft\n${fmtPrice(getDisplayPrice(draftPlan) || 29)} once`,
    `Guardian\n${fmtPrice(getDisplayPrice(guardianPlan) || 15)}/mo`,
    `Concierge\n${fmtPrice(getDisplayPrice(estatePlan) || 39)}/mo`,
  ];

  return (
    <div className="bg-white rounded-[32px] border border-brand-dark shadow-sm overflow-hidden">
      <div className="px-8 py-6 border-b border-brand-dark flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-2xl font-serif text-accent-navy">Full Plan Comparison</h3>
          <p className="text-sm text-text-muted font-light mt-1">See exactly what's included in each tier.</p>
        </div>
        <button onClick={onOpenPricing}
          className="flex items-center gap-2 bg-accent-navy text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all">
          <SafeIcon icon={FiDollarSign} className="w-4 h-4" /> View Pricing
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-dark bg-brand-light">
              <th className="text-left px-6 py-4 text-[10px] font-black uppercase tracking-widest text-text-muted w-1/2">Feature</th>
              {headers.map((h, i) => (
                <th key={i} className={`px-4 py-4 text-center text-[10px] font-black uppercase tracking-widest whitespace-pre-line ${i === 1 ? 'text-accent-gold' : 'text-accent-navy'}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((row, i) => (
              <tr key={row.feature} className={`border-b border-brand-light last:border-0 ${i % 2 === 0 ? '' : 'bg-brand-light/40'}`}>
                <td className="px-6 py-4 text-xs font-bold text-accent-navy">{row.feature}</td>
                <td className="px-4 py-4 text-center"><CellValue val={row.draft} /></td>
                <td className="px-4 py-4 text-center bg-accent-gold/5"><CellValue val={row.guardian} /></td>
                <td className="px-4 py-4 text-center"><CellValue val={row.estate} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── FAQ ────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Do I need a lawyer to make a will?', a: 'While a lawyer is not strictly required in most states, it is strongly recommended for complex estates. Our AI tool generates a draft that a licensed attorney can then review and finalise.' },
  { q: 'Is an AI-generated will legally binding?', a: "An AI draft alone is not legally binding. It must be signed, witnessed (and in some states, notarised) according to your jurisdiction's requirements. Always consult a licensed estate attorney." },
  { q: 'What is a Power of Attorney?', a: 'A Power of Attorney (POA) is a legal document that grants another person the authority to act on your behalf in financial or medical matters, either immediately or upon incapacitation.' },
  { q: 'How often should I update my will?', a: 'It is recommended to review your will every 3–5 years or after any major life event such as marriage, divorce, birth of a child, or significant change in assets.' },
  { q: 'What happens if I die without a will (intestate)?', a: "If you die without a valid will, your state's intestacy laws determine how your assets are distributed, which may not align with your wishes. This process can also be lengthy and costly for your family." },
  { q: 'Can I cancel my subscription at any time?', a: "Yes. Legacy Guardian and Estate Concierge subscriptions can be cancelled at any time from your account settings. You'll retain access until the end of your current billing period." },
  { q: "What's the difference between the one-time draft and a subscription?", a: "The one-time Will Draft gives you a single, downloadable PDF draft. Subscriptions add unlimited revisions, attorney consultations, life-event reminders, and ongoing document management — ensuring your will stays current as your life changes." },
];

// ── Main Component ─────────────────────────────────────────────────────────
const LegalWillPrep = () => {
  const { formatCurrency, convertAmount, currency } = useCurrency();

  const [openFaq,       setOpenFaq]       = useState(null);
  const [isModalOpen,   setIsModalOpen]   = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [billing,       setBilling]       = useState('annual'); // Added billing toggle state

  const [plans,         setPlans]         = useState(FALLBACK_PLANS);
  const [plansLoading,  setPlansLoading]  = useState(true);

  // Convert USD → display currency
  const fmtPrice = useCallback((usd) => {
    if (!usd || usd === 0) return 'Free';
    return formatCurrency(convertAmount(usd, 'USD'));
  }, [formatCurrency, convertAmount]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const all = await fetchActivePlans();
      const willPlans = (Array.isArray(all) ? all : [])
        .filter(p => p.plan_key && p.plan_key.startsWith('will_'))
        .sort((a, b) => a.sort_order - b.sort_order);
      setPlans(willPlans.length > 0 ? willPlans : FALLBACK_PLANS);
    } catch {
      setPlans(FALLBACK_PLANS);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const checklist = [
    { label: 'Draft or update your Last Will & Testament',  done: false },
    { label: 'Designate a Power of Attorney',               done: false },
    { label: 'Create a Healthcare Directive / Living Will', done: false },
    { label: 'List all financial accounts and beneficiaries',done: false },
    { label: 'Secure digital asset passwords in a vault',   done: false },
    { label: 'Notify your executor of document locations',  done: false },
    { label: 'Review life insurance policies and beneficiaries', done: false },
    { label: 'Register your will with a local probate court',done: false },
  ];
  const [checks, setChecks] = useState(checklist);
  const toggleCheck = (i) => setChecks(c => c.map((item, idx) => idx === i ? { ...item, done: !item.done } : item));
  const doneCount = checks.filter(c => c.done).length;

  // Derived plan data
  const guardianPlan = plans.find(p => p.plan_key === 'will_guardian') || FALLBACK_PLANS[1];
  const lowestPrice  = Math.min(...plans.map(p => p.price_usd));

  const legalCards = [
    { icon: FiShield,   color: 'bg-blue-50 text-blue-500',         title: 'Privacy & Data Rights',    desc: 'Your estate data is encrypted using AES-256 standards. We never share personal information with third parties without explicit consent.',                                            tag: 'Security'   },
    { icon: FiFileText, color: 'bg-accent-gold/10 text-accent-gold',title: 'Terms of Service',         desc: 'By using OneFinalLegacy, you agree to maintain a respectful environment and accept our platform usage guidelines.',                                                               tag: 'Policy'     },
    { icon: FiGlobe,    color: 'bg-green-50 text-green-500',        title: 'Jurisdiction Compliance',  desc: 'Our platform supports estate documentation across all 50 US states and 30+ international jurisdictions.',                                                                         tag: 'Legal'      },
    { icon: FiLock,     color: 'bg-purple-50 text-purple-500',      title: 'GDPR & CCPA Compliance',   desc: 'Full compliance with global data protection regulations. Request data deletion or export at any time from your account settings.',                                                tag: 'Compliance' },
  ];

  const processSteps = [
    { num: '01', title: 'Personal Info',      desc: 'Your name, address, marital status, and jurisdiction.' },
    { num: '02', title: 'Executor Details',   desc: 'Primary and alternate executor, plus the powers you grant them.' },
    { num: '03', title: 'Beneficiaries',      desc: 'Who inherits, their share percentages, and any special instructions.' },
    { num: '04', title: 'Review & Generate',  desc: 'Confirm everything, then generate your draft instantly.' },
  ];

  return (
    <div className="space-y-12 md:space-y-16">
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: a,
            },
          })),
        })}
      </script>

      {/* Hero Banner */}
      <div className="bg-accent-navy text-white p-8 md:p-14 rounded-[40px] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-accent-gold/10 rounded-full -mr-40 -mt-40 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/5 rounded-full -ml-28 -mb-28 blur-2xl" />
        <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-accent-gold/20 text-accent-gold border border-accent-gold/30 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-6">
              <SafeIcon icon={FiStar} className="w-3 h-3" /> New Feature
            </div>
            <h2 className="text-3xl md:text-5xl font-serif mb-6 leading-tight">
              Plan Your Legacy.<br />
              <span className="text-accent-gold">Protect Your Family.</span>
            </h2>
            <p className="text-white/70 text-lg font-light leading-relaxed mb-8">
              Use our AI-powered will drafting interview to document your final wishes in minutes — legally structured and ready for attorney review.
            </p>
            <div className="flex flex-wrap gap-4 text-[10px] font-black uppercase tracking-widest text-white/60">
              {['AI Will Drafting', 'Guided Interview', 'Legal Checklist', 'Attorney Network', 'GDPR Compliant'].map(f => (
                <div key={f} className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-accent-gold" /> {f}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-3 flex-shrink-0 w-full lg:w-auto">
            <button
              onClick={() => setIsPricingOpen(true)}
              className="group bg-accent-gold hover:bg-accent-gold/90 text-white px-8 py-5 rounded-2xl flex items-center gap-4 transition-all shadow-2xl shadow-accent-gold/30 hover:shadow-accent-gold/50 hover:scale-105"
            >
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiDollarSign} className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="text-[9px] font-black uppercase tracking-widest opacity-80">
                  {plansLoading ? 'Loading…' : `Starting from ${fmtPrice(lowestPrice)}`}
                </p>
                <p className="text-lg font-serif">View Plans & Pricing</p>
              </div>
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="group bg-white/10 hover:bg-white/20 border border-white/20 text-white px-8 py-4 rounded-2xl flex items-center gap-3 transition-all"
            >
              <SafeIcon icon={FiPlay} className="w-5 h-5" />
              <span className="text-sm font-bold">Preview the Interview</span>
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Teaser Section */}
      <div>
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-accent-gold/10 text-accent-gold border border-accent-gold/20 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
            <SafeIcon icon={FiDollarSign} className="w-3 h-3" /> Simple, Transparent Pricing
          </div>
          <h3 className="text-2xl md:text-4xl font-serif text-accent-navy mb-3">
            Plans for Every Stage of Life
          </h3>
          <p className="text-text-muted font-light max-w-xl mx-auto">
            Whether you need a one-time draft or ongoing legal support, we have a plan that fits your needs and budget.
          </p>
          {/* Live currency indicator */}
          {currency !== 'USD' && !plansLoading && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-[10px] text-amber-700 font-bold">
              <SafeIcon icon={FiGlobe} className="w-3 h-3" />
              Prices shown in {currency} · converted from USD
            </div>
          )}
        </div>

        {/* Pricing Toggle */}
        {!plansLoading && (
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2 bg-brand-light border border-brand-dark rounded-2xl p-1.5">
              {['monthly', 'annual'].map(b => (
                <button
                  key={b}
                  onClick={() => setBilling(b)}
                  className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    billing === b
                      ? 'bg-accent-navy text-white shadow-lg shadow-accent-navy/20'
                      : 'text-text-muted hover:text-accent-navy'
                  }`}
                >
                  {b === 'monthly' ? 'Monthly' : 'Annual · Save 20%'}
                </button>
              ))}
            </div>
          </div>
        )}

        {plansLoading ? (
          <div className="flex items-center justify-center py-16 gap-4">
            <div className="w-10 h-10 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
            <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading plans…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, i) => {
              const meta = PLAN_META[plan.plan_key] || {
                icon: FiPackage, iconBg: 'bg-gray-50 text-gray-500',
                tagline: plan.description || '', highlights: [], cta: 'Select Plan',
                ctaStyle: 'bg-accent-navy text-white hover:bg-accent-navy/90', highlight: false,
              };
              return (
                <motion.div key={plan.id || plan.plan_key} transition={{ delay: i * 0.08 }}>
                  <PricingTeaserCard
                    plan={plan} meta={meta} fmtPrice={fmtPrice}
                    onOpenPricing={() => setIsPricingOpen(true)}
                    currency={currency}
                    billing={billing}
                  />
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Annual savings callout — acts as a toggle when on monthly view */}
        {!plansLoading && billing === 'monthly' && (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-6 p-5 bg-green-50 border border-green-100 rounded-2xl flex items-center justify-between flex-wrap gap-4 cursor-pointer hover:bg-green-100 transition-colors"
            onClick={() => setBilling('annual')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 text-green-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-black text-green-800">Save up to 20% with annual billing</p>
                <p className="text-xs text-green-600 font-light">
                  Guardian plan: {fmtPrice(guardianPlan.price_usd * 0.8)}/mo vs {fmtPrice(guardianPlan.price_usd)}/mo monthly
                </p>
              </div>
            </div>
            <button
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-700 transition-all pointer-events-none"
            >
              See Annual Plans <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-[40px] border border-brand-dark shadow-sm p-8 md:p-12">
        <div className="text-center mb-10">
          <p className="text-[10px] font-black uppercase tracking-widest text-accent-gold mb-3">Simple 4-Step Process</p>
          <h3 className="text-2xl md:text-3xl font-serif text-accent-navy">How the AI Interview Works</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {processSteps.map((s, i) => (
            <motion.div key={s.num} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="relative text-center group">
              {i < processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(50%+2rem)] right-0 h-0.5 bg-brand-dark" />
              )}
              <div className="w-16 h-16 bg-accent-navy/5 text-accent-navy rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-accent-navy group-hover:text-white transition-all">
                <span className="text-xl font-black font-serif">{s.num}</span>
              </div>
              <h4 className="font-serif text-lg text-accent-navy mb-2">{s.title}</h4>
              <p className="text-xs text-text-muted font-light leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => setIsPricingOpen(true)}
            className="inline-flex items-center gap-3 bg-accent-gold text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-lg shadow-accent-gold/20"
          >
            <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
            {plansLoading ? 'Loading Plans…' : `Choose a Plan — From ${fmtPrice(lowestPrice)}`}
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-3 border-2 border-accent-navy text-accent-navy px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all"
          >
            <SafeIcon icon={FiCpu} className="w-4 h-4" /> Preview Interview
          </button>
        </div>
      </div>

      {/* Full Comparison Table */}
      <CompareTable plans={plans} fmtPrice={fmtPrice} onOpenPricing={() => setIsPricingOpen(true)} billing={billing} />

      {/* Legal Checklist + Sidebar */}
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white p-8 md:p-12 rounded-[40px] border border-brand-dark shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Estate Planning Checklist</h3>
              <p className="text-sm text-text-muted font-light mt-1">Track the key legal steps to protect your estate.</p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-serif text-accent-gold">{Math.round((doneCount / checks.length) * 100)}%</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">{doneCount}/{checks.length} Done</p>
            </div>
          </div>
          <div className="h-2 bg-brand-light rounded-full mb-8 overflow-hidden">
            <motion.div animate={{ width: `${(doneCount / checks.length) * 100}%` }}
              className="h-full bg-accent-gold rounded-full transition-all duration-500" />
          </div>
          <div className="space-y-3">
            {checks.map((item, i) => (
              <motion.button key={i} onClick={() => toggleCheck(i)} whileTap={{ scale: 0.98 }}
                className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all ${
                  item.done ? 'bg-green-50 border-green-100' : 'bg-brand-light border-brand-dark hover:border-accent-gold'
                }`}>
                <SafeIcon icon={item.done ? FiCheckCircle : FiCircle}
                  className={`w-6 h-6 flex-shrink-0 ${item.done ? 'text-green-500' : 'text-text-muted'}`} />
                <span className={`text-sm font-bold transition-all ${item.done ? 'text-green-700 line-through opacity-70' : 'text-accent-navy'}`}>
                  {item.label}
                </span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* CTA Card */}
          <div className="bg-accent-navy p-8 rounded-[32px] text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-gold/10 to-transparent" />
            <div className="relative z-10">
              <div className="w-14 h-14 bg-accent-gold text-white rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-accent-gold/30">
                <SafeIcon icon={FiDollarSign} className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-serif text-white mb-1">Ready to Start?</h4>
              <p className="text-[10px] font-black text-accent-gold uppercase tracking-widest mb-4">
                {plansLoading ? 'Loading…' : `From ${fmtPrice(lowestPrice)} one-time`}
              </p>
              <p className="text-sm text-white/60 font-light mb-6 leading-relaxed">
                Complete the guided interview in under 10 minutes and download your draft.
              </p>
              <button onClick={() => setIsPricingOpen(true)}
                className="w-full bg-accent-gold hover:bg-accent-gold/90 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 mb-3">
                <SafeIcon icon={FiDollarSign} /> View Plans
              </button>
              <button onClick={() => setIsModalOpen(true)}
                className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2">
                <SafeIcon icon={FiPlay} /> Preview Interview
              </button>
            </div>
          </div>

          {/* Subscription highlights — live from admin */}
          <div className="bg-accent-gold/10 border border-accent-gold/20 p-6 rounded-[32px]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent-gold text-white rounded-xl flex items-center justify-center shadow-lg shadow-accent-gold/30">
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-serif text-lg text-accent-navy">Subscription Benefits</h4>
                <p className="text-[10px] text-text-muted font-light">
                  Legacy Guardian · {plansLoading ? '…' : `${fmtPrice(guardianPlan.price_usd)}/mo`}
                </p>
              </div>
            </div>
            <div className="space-y-2.5">
              {['Unlimited will revisions', '1× attorney consult per year', 'Life-event update reminders', 'Digital asset vault (50 entries)', 'Cancel any time'].map(b => (
                <div key={b} className="flex items-center gap-2.5">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-accent-gold flex-shrink-0" />
                  <span className="text-xs text-accent-navy font-medium">{b}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setIsPricingOpen(true)}
              className="w-full mt-5 bg-accent-navy text-white py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all flex items-center justify-center gap-2">
              <SafeIcon icon={FiArrowRight} /> Start Free Trial
            </button>
          </div>

          {/* Attorney Network */}
          <div className="bg-white border border-brand-dark p-6 rounded-[32px] shadow-sm">
            <div className="w-10 h-10 bg-accent-navy/5 text-accent-navy rounded-xl flex items-center justify-center mb-4">
              <SafeIcon icon={FiUsers} className="w-5 h-5" />
            </div>
            <h4 className="font-serif text-lg text-accent-navy mb-2">Attorney Network</h4>
            <p className="text-sm text-text-muted font-light mb-4 leading-relaxed">
              Connect with a vetted estate attorney to finalise your documents.
            </p>
            <a href="mailto:legal@onefinallegacy.com?subject=Attorney Referral Request"
              className="flex items-center justify-center gap-2 bg-accent-navy text-white py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all">
              <SafeIcon icon={FiMail} /> Request Referral
            </a>
          </div>

          {/* Timelines */}
          <div className="bg-white border border-brand-dark p-6 rounded-[32px] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiClock} className="w-5 h-5" />
              </div>
              <h4 className="font-serif text-lg text-accent-navy">Typical Timelines</h4>
            </div>
            <div className="space-y-4">
              {[
                { label: 'AI Interview & Draft', time: '~10 min',   color: 'bg-green-500'  },
                { label: 'Attorney Review',       time: '3–5 days', color: 'bg-accent-gold' },
                { label: 'Probate Process',       time: '6–18 months', color: 'bg-red-400' },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${row.color}`} />
                    <span className="text-xs font-bold text-accent-navy">{row.label}</span>
                  </div>
                  <span className="text-[10px] font-black text-text-muted uppercase tracking-widest">{row.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Key Terms */}
          <div className="bg-white border border-brand-dark p-6 rounded-[32px] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent-navy/5 text-accent-navy rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiBook} className="w-5 h-5" />
              </div>
              <h4 className="font-serif text-lg text-accent-navy">Key Terms</h4>
            </div>
            <div className="space-y-3">
              {[
                { term: 'Testator',   def: 'The person who makes the will.'              },
                { term: 'Executor',   def: "Person who carries out the will's instructions." },
                { term: 'Beneficiary',def: 'Person or entity who inherits assets.'       },
                { term: 'Probate',    def: 'Legal process to validate a will.'           },
                { term: 'Intestate',  def: 'Dying without a valid will.'                 },
              ].map(({ term, def }) => (
                <div key={term} className="border-b border-brand-light pb-3 last:border-0 last:pb-0">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-gold mb-0.5">{term}</p>
                  <p className="text-xs text-text-muted font-light">{def}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legal Policy Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {legalCards.map((card, i) => (
          <motion.div key={card.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm hover:shadow-xl hover:border-accent-gold transition-all group">
            <div className="flex items-start gap-5">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${card.color}`}>
                <SafeIcon icon={card.icon} className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-serif text-accent-navy">{card.title}</h3>
                  <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 bg-brand-light text-text-muted rounded-full border border-brand-dark">{card.tag}</span>
                </div>
                <p className="text-sm text-text-muted font-light leading-relaxed">{card.desc}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* FAQ Accordion */}
      <div className="bg-white p-8 md:p-12 rounded-[40px] border border-brand-dark shadow-sm">
        <div className="flex items-center gap-4 mb-10">
          <div className="w-12 h-12 bg-accent-navy/5 text-accent-navy rounded-2xl flex items-center justify-center">
            <SafeIcon icon={FiBook} className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-2xl font-serif text-accent-navy">Frequently Asked Questions</h3>
            <p className="text-sm text-text-muted font-light">Common questions about wills, estates, plans, and legal planning.</p>
          </div>
        </div>
        <div className="space-y-4">
          {FAQS.map((faq, i) => (
            <div key={i} className={`rounded-2xl border-2 overflow-hidden transition-all ${openFaq === i ? 'border-accent-gold' : 'border-brand-dark'}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between p-6 text-left">
                <span className="text-sm font-bold text-accent-navy pr-4">{faq.q}</span>
                <SafeIcon icon={FiChevronRight}
                  className={`w-5 h-5 text-accent-gold flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 1 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <p className="px-6 pb-6 text-sm text-text-muted font-light leading-relaxed border-t border-brand-light pt-4">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-accent-navy to-[#1a2a5e] rounded-[40px] p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent-gold/10 rounded-full -mr-32 -mt-32 blur-3xl" />
        <div className="relative z-10">
          <h3 className="text-2xl md:text-3xl font-serif text-white mb-2">Protect Your Legacy Today</h3>
          <p className="text-white/60 font-light">
            {plansLoading
              ? 'Loading plans…'
              : `Start with a ${fmtPrice(plans.find(p => p.plan_key === 'will_draft')?.price_usd ?? 29)} one-time draft or choose a subscription for ongoing peace of mind.`
            }
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 relative z-10 w-full md:w-auto">
          <button
            onClick={() => setIsPricingOpen(true)}
            className="btn-primary py-5 px-8 flex items-center gap-3 whitespace-nowrap justify-center bg-accent-gold hover:bg-accent-gold/90 border-0"
          >
            <SafeIcon icon={FiDollarSign} /> View Plans & Pricing
          </button>
          <a href="mailto:legal@onefinallegacy.com?subject=Legal Consultation Request"
            className="py-5 px-8 flex items-center gap-3 whitespace-nowrap justify-center bg-white/10 hover:bg-white/20 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border border-white/20">
            <SafeIcon icon={FiMail} /> Book Consultation
          </a>
        </div>
      </div>

      {/* Modals */}
      <WillDraftingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <WillPricingModal
        isOpen={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
        onSelectPlan={(planKey) => {
          if (planKey === 'will_draft' || planKey === 'will_guardian' || planKey === 'will_estate') {
            setIsModalOpen(true);
          }
        }}
      />
    </div>
  );
};

export default LegalWillPrep;